import React, { useMemo } from 'react';
import { Stack } from 'new/elements/Stack';
import { signatureMethods } from 'new/components/settings/VendorBuilder/selectTypes';
import {
  useVendorBuilder,
  VendorType,
} from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';
import useAuthParamsInjector from 'new/components/settings/VendorBuilder/AuthConfig/useAuthParamsInjector';
import { InjectAuthParamType } from 'new/components/settings/VendorBuilder/types';
import { Typography } from '@mui/material';

interface TokenProps {
  index: number;
}

const getOAuth12LeggedParams = (vendor) =>
  [
    {
      configName: 'oauth1.consumer.key',
      param: {
        name: vendor.name + '.consumer_key',
        display_name: 'OAuth1 Consumer Key',
        description: `${vendor.display_name} OAuth1 Consumer Key`,
        order: 1,
        data_type: 'string',
        config: {
          required: true,
        },
        secured: false,
      },
    },
    {
      configName: 'oauth1.consumer.secret',
      param: {
        name: vendor.name + '.consumer_secret',
        display_name: 'OAuth1 Consumer Secret',
        description: `${vendor.display_name} OAuth1 Consumer Secret`,
        order: 2,
        data_type: 'string',
        config: {
          required: true,
          type: 'password',
        },
        secured: true,
      },
    },
    {
      // configName: 'oauth1.access.token',
      param: {
        name: vendor.name + '.access_token',
        display_name: 'OAuth1 Access Token',
        description: `${vendor.display_name} OAuth1 Access Token`,
        order: 3,
        data_type: 'string',
        config: {
          required: true,
        },
        secured: true,
      },
    },
    {
      // configName: 'oauth1.access.token.secret',
      param: {
        name: vendor.name + '.access_token_secret',
        display_name: 'OAuth1 Access Token Secret',
        description: `${vendor.display_name} OAuth1 Token Secret`,
        order: 4,
        data_type: 'string',
        config: {
          required: true,
          type: 'password',
        },
        secured: true,
      },
    },
  ] as InjectAuthParamType[];

const getOAuth13LeggedParams = (vendor) =>
  [
    {
      param: {
        name: 'oauth1a.request_url',
        display_name: 'URL for Request Token',
        description: `First step of the 3-legged OAuth1.0 flow.`,
        order: 1,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: '',
        },
      },
    },
    {
      configName: 'oauth1.consumer.key',
      param: {
        name: 'oauth1a.request_url_param.oauth_consumer_key',
        display_name: 'Consumer Key: for Request URL',
        description: `OAuth1 Consumer Key for adding to the request url`,
        order: 2,
        data_type: 'string',
        config: {
          default: '',
        },
      },
    },
    {
      configName: 'oauth1.consumer.secret',
      param: {
        name: 'oauth1a.request_url_param.oauth_consumer_secret',
        display_name: 'Consumer Secret: For Request URL',
        description: `OAuth1 Consumer Secret for adding to the request url`,
        order: 3,
        data_type: 'string',
        config: {
          default: '',
        },
        secured: true,
      },
    },
    {
      param: {
        name: 'oauth1a.auth_url',
        display_name: 'Authorization URL',
        description: `This is the second step of the 3-legged OAuth1.0 flow.`,
        order: 4,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: '',
        },
      },
    },
    {
      param: {
        name: 'oauth1a.token_url',
        display_name: 'Access Token URL',
        description: `Token URL used to convert the request token into an access token. This is the final step of the 3-legged OAuth1.0 flow.`,
        order: 6,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: '',
        },
        secured: true,
      },
    },
    {
      param: {
        name: 'oauth1a.token_url_param.oauth_consumer_key',
        display_name: 'Consumer Key: for Token URL',
        description: `OAuth1 Consumer Key for adding to the token url`,
        order: 7,
        data_type: 'string',
        config: {
          param_ref: 'oauth1a.request_url_param.oauth_consumer_key',
          isHiddenField: true,
        },
        secured: true,
      },
    },
  ] as InjectAuthParamType[];

const oauth1Fields = (index, is3Legged = false, vendorName = '') => [
  {
    name: `auth_templates[${index}].config["oauth1.signature.method"]`,
    label: (
      <>
        Signature method <code>config["oauth1.signature.method"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        Some API vendors require requests to be signed with a dynamic signature algorithm. Select
        the signature method that should be used to sign the requests. Use <code>PLAINTEXT</code> if
        this is not specified by vendor.
        <br />
        Default Value: <code>PLAINTEXT</code>
      </Typography>
    ),
    type: 'select',
    required: true,
    defaultValue: signatureMethods[0].value,
    items: signatureMethods,
  },
  {
    name: `auth_templates[${index}].config["oauth1.extra.parameters"]`,
    label: (
      <>
        OAuth1 Advanced Configuration (optional) <code>config["oauth1.extra.parameters"]</code>
      </>
    ),
    type: 'text',
    description: (
      <Typography padding="16px">
        Enter any other advanced configuration parameters like Realm, Version, etc. that might be
        required. This information should be entered as a valid JSON in this input box. In the
        template it will be saved as JSON escaped string.
        <br />
        Example: Enter <code>&#123;"realm":"$&#123;netsuite_rest_api.realm&#125;"&#125;</code> and
        see it saved in spec as:
        <code>"&#123;\"realm\":\"$&#123;netsuite_rest_api.realm&#125;\"&#125;"</code>
      </Typography>
    ),
  },
  {
    name: `auth_templates[${index}].config["oauth1.access.token"]`,
    label: (
      <>
        Access Token for 3-Legged OAuth <code>config["oauth1.access.token"]</code>
      </>
    ),
    // dependsOn: {
    //   [`auth_templates[${index}].config['oauth1.token_exchange_type']`]: '3-legged'
    // },
    description: (
      <Typography padding="16px">
        The access token received when you made the first token request as part of the 3-legged
        OAuth token fetching sequence. For 3-Legged OAuth, this is included in the Token URL
        Response. Set this as follows:{' '}
        <code>$&#123;oauth1a.json_path_of_access_token_in_token_response&#125;</code>
        <br />
        So if token response contains <code>access_token</code>, set this to{' '}
        <code>$&#123;oauth1a.access_token&#125;</code>
      </Typography>
    ),
    defaultValue: is3Legged ? `\${oauth1a.access_token}` : `\${${vendorName}.access_token}`,
    required: true,
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["oauth1.access.token.secret"]`,
    label: (
      <>
        Access Token for 3-Legged OAuth <code>config["oauth1.access.token.secret"]</code>
      </>
    ),
    // dependsOn: {
    //   [`auth_templates[${index}].config['oauth1.token_exchange_type']`]: '3-legged'
    // },
    description: (
      <Typography padding="16px">
        The access token secret received when you made the first token request as part of the
        3-legged OAuth token fetching sequence. For 3-Legged OAuth, this is included in the Token
        URL Response. Set this as follows:{' '}
        <code>$&#123;oauth1a.json_path_of_access_token_secret_in_token_response&#125;</code>
        <br />
        So if token response contains <code>oauth_token_secret</code>, set this to{' '}
        <code>$&#123;oauth1a.oauth_token_secret&#125;</code>
      </Typography>
    ),
    defaultValue: is3Legged
      ? `\${oauth1a.oauth_token_secret}`
      : `\${${vendorName}.access_token_secret}`,
    required: true,
    type: 'text',
  },
];

export const ConfigOAuth1 = (props: TokenProps) => {
  const { index } = props;
  const { vendor, updateVendor, addNameChangeListener } = useVendorBuilder();

  const exchangeType = vendor.auth_templates?.[index]?.config?.['oauth1.token_exchange_type'];

  const is3Legged = exchangeType === '3-legged' ? true : false;

  const twoLeggedParams = useMemo(() => getOAuth12LeggedParams(vendor), [vendor.name]);
  const threeLeggedParams = useMemo(() => getOAuth13LeggedParams(vendor), [vendor.name]);
  const params = exchangeType === '2-legged' ? twoLeggedParams : threeLeggedParams;

  useAuthParamsInjector(params, index);

  const auth1Fields = useMemo(
    () => oauth1Fields(index, is3Legged, vendor.name),
    [index, is3Legged, vendor.name],
  );

  return (
    <Stack gap="16px">
      <FormBuilder fields={auth1Fields} />
    </Stack>
  );
};
